import type {Paginated} from '../generated/graphql';

/**
 * Types of learning products
 */
export enum LearningProductType {
    COURSE = 'COURSE',
    PRACTICE = 'PRACTICE',
    ASSESSMENT = 'ASSESSMENT',
}

/**
 * User profile fields used in reviews, omitting fields that are not available in the API response
 * Using the GraphQL UserProfile type since we eventually want to adopt that here
 */
export type ReviewsUserProfile = {
    name: string;
    images: {
        px50x50: string;
    };
    tracking_id: string;
    display_name: string;
    image_50x50: string;
};

/**
 * A response to a review
 */
export interface ReviewResponse {
    /**
     * ID of the response
     */
    id: string;

    /**
     * Content of the response
     */
    content?: string;

    /**
     * User who authored the response
     */
    user: ReviewsUserProfile;

    /**
     * When the response was created
     */
    createdAt: string;

    /**
     * When the response was last updated
     */
    updatedAt?: string;
}

/**
 * A review for a reviewable entity
 */
export interface Review extends Paginated {
    /**
     * ID of the review
     */
    id: string;

    /**
     * Type of the reviewable learning product
     */
    type: LearningProductType;

    /**
     * Content of the review
     */
    content?: string;

    /**
     * Rating given in this review (1-5)
     */
    rating?: number;

    /**
     * User who authored the review
     */
    user: ReviewsUserProfile;

    /**
     * When the review was created
     */
    createdAt: string;

    /**
     * When the review was last updated
     */
    updatedAt?: string;

    /**
     * Instructor response to this review, if any
     */
    response?: ReviewResponse;
}

/**
 * Sorting options for course reviews
 */
export enum ReviewSortType {
    /**
     * Most recent reviews first
     */
    CREATE_TIME = 'CREATE_TIME',

    /**
     * Ranked review scores from highest to lowest
     */
    SCORE_RANK = 'SCORE_RANK',
}

/**
 * Filters for course reviews
 */
export interface ReviewFilters {
    /**
     * Text to search within review content
     */
    searchText?: string;
}

/**
 * Parameters for fetching reviews by course ID
 */
export interface ReviewsByCourseIdParams {
    courseId: string;
    page?: number;
    pageSize?: number;
    filters?: ReviewFilters;
    sortOptions?: ReviewSortType[];
}

/**
 * Result of the reviewsByCourseId query
 */
export interface ReviewsByCourseIdResult {
    reviewsByCourseId: Review[];
}
